import { Building2, Star, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { NavLink } from '@/components/NavLink';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@pps/ui/components/ui/sidebar";
import type { Club } from '@/types/crm';
import type { ViewScope } from '@/types/viewScope';

declare global {
  interface Window {
    DEBUG_VIEWSCOPE?: boolean;
  }
}

/**
 * Switcher de escopo de visualizacao (Bloco A.2 do plano sprint apr/2026 v2).
 * Renderiza no topo do sidebar quando o usuario opera em mais de um clube
 * da mesma rede OU quando e network admin. Permite alternar entre "Toda a
 * Rede" (agregador) e cada clube individualmente.
 *
 * Gate de plano: visualizar rede existente NAO eh gated por plano. Se o
 * usuario tem rede montada (network de clubes OU eh network admin), o
 * switcher aparece independente do plan_tier do clube atual — isso evita
 * que um downgrade temporario deixe o operador cego. O gate Enterprise
 * vive em /settings/network (tela de adicionar filial / criar rede).
 */
export function NetworkScopeSwitcher() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { t } = useTranslation();
  const {
    viewScope,
    setViewScope,
    networkClubs,
    isNetworkAdmin,
    club,
  } = useAuth();

  // Regra unica: so renderiza se ha rede de verdade (>= 2 clubes) OU se o
  // usuario e network admin (pode estar montando rede agora). Sem rede,
  // sem switcher — independente do plan_tier.
  if (networkClubs.length <= 1 && !isNetworkAdmin) return null;

  // parentId canonico da rede para montar o scope de "Toda a Rede".
  const networkParentId = club?.parent_club_id ?? club?.id ?? '';

  // Ordem estavel: pai primeiro, depois filhos alfabeticamente. Evita que
  // a lista pule de posicao entre renders.
  const orderedClubs: Club[] = [...networkClubs].sort((a, b) => {
    if (a.id === networkParentId) return -1;
    if (b.id === networkParentId) return 1;
    return a.name.localeCompare(b.name);
  });

  const isClubActive = (clubId: string): boolean =>
    viewScope.type === 'club' && viewScope.clubId === clubId;
  const isNetworkActive = viewScope.type === 'network';

  // DIAG TEMP (fix/viewscope-diag-logs): log gated por window.DEBUG_VIEWSCOPE
  // para diagnosticar PR #192 follow-up. Ativar no DevTools com
  // `window.DEBUG_VIEWSCOPE = true`. Remover após confirmar root cause.
  const debugLog = (label: string, payload: unknown): void => {
    if (typeof window !== 'undefined' && window.DEBUG_VIEWSCOPE) {
      // eslint-disable-next-line no-console
      console.log(`[viewScope][switcher] ${label}`, payload);
    }
  };

  const handleSelectClub = (c: Club): void => {
    const next: ViewScope = { type: 'club', clubId: c.id };
    debugLog('handleSelectClub → setViewScope', {
      next,
      currentViewScope: viewScope,
      networkClubIds: networkClubs.map((x) => x.id),
      isNetworkAdmin,
    });
    setViewScope(next);
  };

  const handleSelectNetwork = (): void => {
    if (!networkParentId) {
      debugLog('handleSelectNetwork ABORT (no networkParentId)', { club, networkClubs });
      return;
    }
    const next: ViewScope = { type: 'network', parentId: networkParentId };
    debugLog('handleSelectNetwork → setViewScope', {
      next,
      currentViewScope: viewScope,
      networkClubIds: networkClubs.map((x) => x.id),
      isNetworkAdmin,
    });
    setViewScope(next);
  };

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[10px] font-semibold tracking-widest uppercase text-[hsl(var(--sidebar-muted))] px-3 mb-1 flex items-center gap-1.5">
        <Building2 className="h-3 w-3" />
        {!collapsed && <span>{t('nav.network')}</span>}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {isNetworkAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={isNetworkActive}
                onClick={handleSelectNetwork}
                className={`rounded-lg transition-colors ${
                  isNetworkActive
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent'
                }`}
              >
                <Star
                  className={`h-4 w-4 ${isNetworkActive ? 'fill-current' : ''}`}
                />
                {!collapsed && <span>{t('nav.allNetwork')}</span>}
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {orderedClubs.map((c) => {
            const active = isClubActive(c.id);
            return (
              <SidebarMenuItem key={c.id}>
                <SidebarMenuButton
                  isActive={active}
                  onClick={() => handleSelectClub(c)}
                  className={`rounded-lg transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent'
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full shrink-0 ${
                      active
                        ? 'bg-primary-foreground'
                        : 'border border-current opacity-60'
                    }`}
                    aria-hidden
                  />
                  {!collapsed && <span className="truncate">{c.name}</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}

          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="rounded-lg text-[hsl(var(--sidebar-muted))] hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <NavLink to="/settings/network" activeClassName="">
                <Plus className="h-4 w-4" />
                {!collapsed && (
                  <span className="text-xs">{t('nav.manageNetwork')}</span>
                )}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
